import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PackageCheck, X, Paperclip, ShieldAlert, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * FinalBillsModal — Phase 10, `accounts_review` stage for Material/Service v10.0.
 * Actor: Amey.G (or super_admin).
 *
 * Rules:
 *   • Only `mark_fulfilled` action supported (single button).
 *   • Final Bills: 3 upload slots (Bill 1/2/3). At least 1 required.
 *   • Remarks optional.
 */
export default function FinalBillsModal({
  open, onClose, onDone, requisition, actingAsBanner = null,
}) {
  const [remarks, setRemarks] = useState("");
  const [files, setFiles] = useState([null, null, null]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemarks(""); setFiles([null, null, null]); setError("");
    setTimeout(() => textareaRef.current?.focus(), 20);
  }, [open, requisition?.ref_code]);

  if (!open || !requisition) return null;

  const trimmedRemarks = remarks.trim();
  const filled = files.filter(Boolean);
  const canSubmit = filled.length >= 1 && !busy;

  const setFileAt = (i, f) => setFiles((prev) => {
    const copy = [...prev]; copy[i] = f || null; return copy;
  });

  const uploadOne = async (file, slotIdx) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("field_name", `final_bill_${slotIdx + 1}`);
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return {
      field_name: `final_bill_${slotIdx + 1}`,
      filename: data.filename, url: data.url, size: data.size,
      kind: "final_bill",
    };
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const uploaded = [];
      for (let i = 0; i < 3; i++) {
        if (files[i]) {
          try {
            const att = await uploadOne(files[i], i);
            uploaded.push(att);
          } catch {
            throw new Error(`Bill ${i + 1} upload failed. Please retry.`);
          }
        }
      }
      const payload = { action: "mark_fulfilled", remarks: trimmedRemarks,
                         attachments: uploaded };
      const { data } = await api.post(`/requisitions/${requisition.ref_code}/action`, payload);
      toast.success(`Fulfilled — ${requisition.ref_code}`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = err?.message?.startsWith?.("Bill") ? err.message : formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         onClick={onClose} data-testid="final-bills-modal">
      <div className="w-full max-w-xl bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden max-h-[92vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <PackageCheck className="text-[#7a1f2b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Accounts · Mark Fulfilled — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>You are acting as <strong>{actingAsBanner}</strong> on behalf of the assigned approver.</div>
            </div>
          )}
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#7a1f2b] px-3 py-2 rounded">
            <strong>Upload final bills (at least 1) and close the requisition.</strong> Final bills are archived and shared with the applicant.
          </div>
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">{error}</div>
          )}

          <div>
            <div className="text-sm font-medium text-[#1f2320] mb-2">
              Final Bills Attachments <span className="text-[#b3261e]">*</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">at least 1 required</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              {[0, 1, 2].map((i) => (
                <label key={i}
                       className={`flex items-center gap-2 px-3 py-2 rounded-md border border-dashed cursor-pointer transition-colors ${files[i] ? "border-[#7a1f2b] bg-[#f9e8ec]" : "border-[#cbbba0] hover:border-[#7a1f2b]"}`}
                       data-testid={`final-bill-slot-${i + 1}`}>
                  <Paperclip size={14} className="text-[#7a1f2b] shrink-0" />
                  <div className="min-w-0 text-xs">
                    <div className="font-semibold text-[#1f2320]">Bill {i + 1}</div>
                    <div className="text-[#6e7770] truncate">
                      {files[i]?.name || "Choose file"}
                    </div>
                  </div>
                  <input type="file" className="hidden"
                         accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                         onChange={(e) => setFileAt(i, e.target.files?.[0])} />
                </label>
              ))}
            </div>
            {filled.length === 0 && (
              <div className="mt-2 text-xs text-[#8a6a2f] inline-flex items-center gap-1">
                <AlertTriangle size={12} /> Upload at least one bill to fulfil.
              </div>
            )}
          </div>

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks <span className="text-[#6e7770] text-xs font-normal">(optional)</span>
            </span>
            <textarea ref={textareaRef} value={remarks}
                      onChange={(e) => setRemarks(e.target.value.slice(0, 1000))} rows={3}
                      placeholder="Add any note for the record (optional)…"
                      className="input-vgws min-h-[80px]"
                      data-testid="final-bills-remarks" />
          </label>
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="final-bills-cancel">Cancel</button>
          <button onClick={submit} disabled={!canSubmit}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm bg-[#7a1f2b] hover:bg-[#5a1520] border-[#5a1520] ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="final-bills-confirm">
            {busy
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <PackageCheck size={16} />}
            {busy ? "Please wait…" : "Confirm Fulfilled"}
          </button>
        </div>
      </div>
    </div>
  );
}
